import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppointmentResumoResponse, AgendamentoItem } from '../../../interfaces/appointment.interface';
import { AppointmentsService } from '../../../services/appointments.service';
import { LocalStorageUtil } from '../../../utils/local-storage.util';
import { SelectedDnService } from '../../../services/selected-dn.service';
import { OauthResult, Dealership } from '../../../interfaces/oauth.interface';
import { TrackClickDirective } from '../../../directives/track-click.directive';
import { ConfigService } from '../../../services/config.service';
import { AdminPanelAccessService } from '../../../services/admin-panel-access.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TrackClickDirective,
    MatTooltipModule,
    MatIconModule
  ],
  templateUrl: './header.html',
  styleUrls: ['./header.scss']
})
export class Header implements OnInit, OnDestroy {
  private dnSubscription?: Subscription;

  showAvatarPopup = false;
  showCalendarPopup = false;
  currentRoute: string = '';
  showMobileMenu = false;
  isMobile = false;

  /** Expõe o flag de fase 2 para o template */
  get isPhase2(): boolean {
    return this.configService.isPhase2Enabled();
  }

  // Dados do serviço
  appointmentData: AppointmentResumoResponse | null = null;
  loading = false;
  error = false;
  user: OauthResult | null = null;
  selectedUnit: string = '';
  allDealerships: Dealership[] = [];
  canAccessAdminPanel = false;

  constructor(
    private router: Router,
    private appointmentsService: AppointmentsService,
    private selectedDnService: SelectedDnService,
    private adminPanelAccessService: AdminPanelAccessService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef
  ) {
    // Observar mudanças de rota
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe((event: any) => {
        this.currentRoute = event.url;
      });
  }

  ngOnInit(): void {
    // Carrega os dados do usuario
    try {
      this.user = LocalStorageUtil.getVWUser();

      // Monta lista plana de todas as concessionárias
      this.allDealerships = this.getAllDealerships();

      // Obter DN do serviço
      let currentDn = this.selectedDnService.getSelectedDn();

      // Se não houver DN selecionado, usar o primeiro disponível
      if (!currentDn && this.allDealerships.length > 0) {
        currentDn = String(this.allDealerships[0].codigo);
        this.selectedDnService.setSelectedDn(currentDn);
      }

      // Garantir que o selectedUnit seja uma string para comparação correta
      // E que corresponda a um dos DNs disponíveis
      this.selectedUnit = String(currentDn || '');

      // Verificar se o DN selecionado está na lista de concessionárias
      if (this.allDealerships.length > 0) {
        const dnExists = this.allDealerships.some(d => String(d.codigo) === this.selectedUnit);
        if (!dnExists) {
          // Se não existir, usar o primeiro disponível
          this.selectedUnit = String(this.allDealerships[0].codigo);
          this.selectedDnService.setSelectedDn(this.selectedUnit);
        }
      }

      // Forçar detecção de mudanças para atualizar o select
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Erro ao carregar dados do usuário:', e);
    }

    // Subscreve às mudanças do DN (caso mude de outra forma)
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe((dn: string) => {
      if (dn) {
        const dnString = String(dn);
        if (dnString !== this.selectedUnit) {
          this.selectedUnit = dnString;
          this.cdr.detectChanges();
        }
      }
    });

    // Carrega os dados quando o componente é inicializado
    this.loadAppointments();

    // Exibe atalho de admin-panel apenas para promotor com permissão PLLEADCC
    this.loadAdminPanelAccess();

    // Verifica se é mobile
    this.checkMobile();
  }

  ngOnDestroy(): void {
    // Limpa a subscription para evitar memory leaks
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
    // Remove event listener e restaura o scroll
    window.removeEventListener('resize', this.handleResize.bind(this));
    document.body.style.overflow = '';
  }

  // ===== MÉTODO PARA VERIFICAR MOBILE =====
  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize(): void {
    this.isMobile = window.innerWidth <= 768;
    if (!this.isMobile) {
      this.showMobileMenu = false;
      document.body.style.overflow = '';
    }
    this.cdr.detectChanges();
  }

  // ===== MÉTODOS DO MENU MOBILE =====
  toggleMobileMenu(): void {
    this.showMobileMenu = !this.showMobileMenu;
    if (this.showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  closeMobileMenu(): void {
    this.showMobileMenu = false;
    document.body.style.overflow = '';
  }

  // ===== MÉTODO CORRIGIDO PARA NAVEGAÇÃO DO RADAR =====
  navigateToRadar(): void {
    const token = this.getAuthToken();
    const dn = this.selectedUnit || '9999'; // Usa o DN selecionado ou 9999 como fallback

    // Lógica corrigida conforme solicitado:
    if (this.isInReportsRoute()) {
      // Se está na rota /reports, vai para Plataforma Digital de Reports
      const redirectUrl = this.buildPlatformUrl('reports', dn, token);
      window.open(redirectUrl, '_self');
    } else if (this.isInLeadsRoute()) {
      // Se está na rota /leads, vai para Plataforma Digital de Leads
      const redirectUrl = this.buildPlatformUrl('leads', dn, token);
      window.open(redirectUrl, '_self');
    } else {
      // Para qualquer outra rota, vai para Plataforma Digital de Reports (comportamento padrão)
      const redirectUrl = this.buildPlatformUrl('reports', dn, token);
      window.open(redirectUrl, '_self');
    }
  }

  // ===== MÉTODOS AUXILIARES PARA O RADAR =====

  /**
   * Verifica se está na rota de Reports
   */
  isInReportsRoute(): boolean {
    return this.currentRoute.includes('/reports');
  }

  isInAdminRoute(): boolean {
    const route = this.currentRoute || this.router.url;
    return route.includes('/admin-panel');
  }

  /**
   * Verifica se está na rota de Leads
   */
  private isInLeadsRoute(): boolean {
    return this.currentRoute.includes('/leads') ||
           this.currentRoute.includes('/calendar') ||
           this.currentRoute.includes('/dashboard');
  }

  /**
   * Verifica se deve mostrar o calendário de agendamentos
   * Retorna true apenas quando está na plataforma leads
   */
  shouldShowCalendar(): boolean {
    return this.isInLeadsRoute();
  }

  /**
   * Obtém o token de autenticação do localStorage
   */
  private getAuthToken(): string {
    try {
      const user = LocalStorageUtil.getAccessToken();
      return user || 'xpto'; // Fallback para 'xpto'
    } catch {
      return 'xpto';
    }
  }

  /**
   * Constrói a URL da Plataforma Digital
   */
  private buildPlatformUrl(redirect: string, dn: string, token: string): string {
    const baseUrl = this.configService.getRequiredByPath<string>('bancoVw.platformLoginUrl');

    return `${baseUrl}?token=${token}&codigoConcessionaria=${dn}`;
  }

  onUnitChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const newDn = select.value;

    // Usar o serviço para atualizar (isso notificará todos os componentes)
    this.selectedDnService.setSelectedDn(newDn);
    this.selectedUnit = newDn;

    // Recarregar agendamentos
    this.loadAppointments();
  }

  onUnitChangeModel(newDn: string): void {
    // Garantir que seja string
    const dnString = String(newDn);

    // Usar o serviço para atualizar (isso notificará todos os componentes)
    this.selectedDnService.setSelectedDn(dnString);
    this.selectedUnit = dnString;

    // Recarregar agendamentos
    this.loadAppointments();
  }

  loadAppointments(): void {
    this.loading = true;
    this.error = false;

    this.appointmentsService.getAppointmentsResumo(this.selectedUnit).subscribe({
      next: (data) => {
        this.appointmentData = data;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar agendamentos:', error);
        this.error = true;
        this.loading = false;
      }
    });
  }

  // Método para obter a imagem do logo baseado na rota
  getLogoImage(): string {
    if (this.currentRoute.includes('/reports')) {
      return '/assets/images/logo_novo_3.svg';
    }
    return '/assets/images/logo_novo_3.svg';
  }

  // Método para obter o ícone de reports baseado na rota
  getReportsIcon(): string {
    if (this.currentRoute.includes('/reports')) {
      return 'assets/icons/iconConversion.png'; // Ícone diferente quando já está em /reports
    }
    return 'assets/icons/reportsIcon.png'; // Ícone normal
  }

  // Método para obter o routerLink do ícone de reports
  getReportsRouterLink(): string {
    if (this.currentRoute.includes('/reports')) {
      return '/dashboard'; // Quando já está em /reports, clica para voltar ao dashboard
    }
    return '/reports'; // Quando não está em /reports, clica para ir para /reports
  }

  // Método para obter o routerLink do logo
  getLogoRouterLink(): string | null {
    if (this.currentRoute.includes('/reports')) {
      return '/reports'; // Na rota /reports, não é clicável
    }
    return '/dashboard'; // Nas outras rotas, redireciona para dashboard
  }

  // Método para obter o cursor do logo
  getLogoCursor(): string {
    return 'pointer'; // Nas outras rotas, cursor de pointer
  }

  // Métodos para acessar os dados da API com formatação
  getTodaySchedules(): AgendamentoItem[] {
    if (!this.hasAppointmentsData()) {
      return [];
    }

    // Limita a 5 itens
    return this.appointmentData!.agendadosParaHoje.agendamentos.slice(0, 5);
  }

  getTodayTotal(): number {
    if (!this.hasAppointmentsData()) {
      return 0;
    }
    return this.appointmentData!.agendadosParaHoje.total;
  }

  getUpcomingTotal(): number {
    if (!this.hasAppointmentsData()) {
      return 0;
    }
    return this.appointmentData!.proximosAgendamentos.total;
  }

  getPastTotal(): number {
    if (!this.hasAppointmentsData()) {
      return 0;
    }
    return this.appointmentData!.agendamentosPassados.total;
  }

  hasAppointmentsData(): boolean {
    return !!this.appointmentData &&
           !!this.appointmentData.agendadosParaHoje &&
           !!this.appointmentData.proximosAgendamentos &&
           !!this.appointmentData.agendamentosPassados;
  }

  hasNoAppointments(): boolean {
    if (!this.hasAppointmentsData()) {
      return true;
    }

    return this.getTodayTotal() === 0 &&
           this.getUpcomingTotal() === 0 &&
           this.getPastTotal() === 0;
  }

  shouldShowAdminPanelIcon(): boolean {
    return this.isPhase2 && this.canAccessAdminPanel && !this.isInAdminRoute() && !this.isInReportsRoute();
  }

  private loadAdminPanelAccess(): void {
    this.adminPanelAccessService.getAccess().subscribe({
      next: (result) => {
        this.canAccessAdminPanel = !!result?.canAccessAdminPanel;
        this.cdr.detectChanges();
      },
      error: () => {
        this.canAccessAdminPanel = false;
        this.cdr.detectChanges();
      }
    });
  }

  toggleAvatarPopup(): void {
    this.showAvatarPopup = !this.showAvatarPopup;
  }

  toggleCalendarPopup(): void {
    this.showCalendarPopup = !this.showCalendarPopup;
    this.closeMobileMenu();
    // Recarrega os dados quando o popup é aberto para garantir dados atualizados
    if (this.showCalendarPopup) {
      this.loadAppointments();
    }
  }

  closeCalendarPopup(): void {
    this.showCalendarPopup = false;
  }

  closeAvatarPopup(): void {
    this.showAvatarPopup = false;
  }

  // ===== MÉTODOS DE NAVEGAÇÃO SIMPLIFICADOS =====

  viewTodayAll(): void {
    this.closeCalendarPopup();
    this.router.navigate(['/calendar/day']);
  }

  viewUpcomingAll(): void {
    this.closeCalendarPopup();
    this.router.navigate(['/calendar/week']);
  }

  viewPastAll(): void {
    this.closeCalendarPopup();
    this.router.navigate(['/calendar/month']);
  }

  viewAgenda(): void {
    this.closeCalendarPopup();
    // Navega para a rota padrão de appointments (que redireciona para day)
    this.router.navigate(['/calendar']);
  }

  // Método para formatar o horário do agendamento
  formatHorario(item: AgendamentoItem): string {
    if (!item.horario) return '';

    // Converte o horário para formato HH:MM se for uma string de data
    if (item.horario.includes('T')) {
      const date = new Date(item.horario);
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return item.horario;
  }

  // Método para navegar para detalhes do lead
  navigateToLeadDetails(item: AgendamentoItem): void {
    // Fecha o popup do calendário
    this.closeCalendarPopup();

    // Verifica se tem leadId no item
    if (item.idLead) {
      // Navega para a rota: /leads/{leadId}/detalhes
      this.router.navigate(['/leads', item.idLead, 'detalhes']);
    } else {
      if (this.configService.isDebugEnabled()) {
        console.warn('LeadId não encontrado no agendamento:', item);
      }
      // Opcional: mostrar uma mensagem para o usuário
      alert('Não foi possível encontrar o ID do lead');
    }
  }

  /**
   * Obtém todas as concessionárias de todas as montadoras em uma lista plana
   */
  getAllDealerships(): Dealership[] {
    if (!this.user?.montadoras) {
      return [];
    }
    return this.user.montadoras.flatMap(manufacturer => manufacturer.concessionarias);
  }

  // Adicione esta propriedade na classe
showDnModal = false;
private tempSelectedUnit: string = '';

// Adicione estes métodos

openDnModal(): void {
  this.tempSelectedUnit = this.selectedUnit;
  this.showDnModal = true;
  document.body.style.overflow = 'hidden';
}

closeDnModal(): void {
  this.showDnModal = false;
  document.body.style.overflow = '';
}

selectDnFromModal(dn: string): void {
  this.tempSelectedUnit = dn;
}

confirmDnSelection(): void {
  if (this.tempSelectedUnit !== this.selectedUnit) {
    this.onUnitChangeModel(this.tempSelectedUnit);
  }
  this.closeDnModal();
}

getSelectedDealershipName(): string {
  if (!this.user?.montadoras || !this.selectedUnit) {
    return 'Selecione uma concessionária';
  }

  for (const manufacturer of this.user.montadoras) {
    for (const dealership of manufacturer.concessionarias) {
      if (dealership.codigo.toString() === this.selectedUnit) {
        return `${dealership.codigo.toString().padStart(5, '0')} - ${dealership.nome}`;
      }
    }
  }

  return 'Selecione uma concessionária';
}
}
